import { Buffer, Container, Geometry, Mesh, Point, Shader, Texture, WRAP_MODES } from "pixi.js"
import { NightFogConfig, TileSize } from "game-common/models"
import { nightFraction } from "./day_gate"

// Three drifting layers of the same noise field, at different sizes and at different fractions of the
// camera. `parallax` is what sells depth: 1 is locked to the ground (the bank sits still in the world
// as you walk through it), 0 is locked to the screen (infinitely far off), and between the two a layer
// slides against the ground at its own rate. In a top-down orthographic view there is no true
// parallax for a plane above the floor -- translating the camera moves every height equally -- so a
// layer that lags the world is the whole of the illusion of height, and these three read as ground
// mist, a bank at waist height, and far haze.
//
// The sizes are set against what is actually on screen -- about 12 x 9 tiles at the world surface's 2x
// zoom -- and the noise field's own 3x3 lattice means one texture tile already holds about three
// lobes. So a 9-tile layer draws lobes of roughly three tiles, four of them across the view. Sizes
// much above the screen were the first version's mistake: a feature wider than the viewport cannot
// read as a wisp, only as an overall gradient, which is precisely what a hazy filter looks like.
const LAYERS: Array<{ size: number; parallax: number }> = [
    { size: 14 * TileSize, parallax: 0.34 },
    { size: 9 * TileSize, parallax: 0.66 },
    { size: 5 * TileSize, parallax: 1.0 },
]

// The noise bake. 128 is plenty: fog is low-frequency by nature and every layer stretches this across
// at least seven tiles, so the texture is magnified, never minified. Power of two because WebGL 1 only
// wraps (GL_REPEAT) power-of-two textures, and the whole design rests on wrapping.
const NOISE_SIZE = 128
// Lattice frequencies summed into each field, coarsest first, with the weights beside them. Kept few
// and coarse ON PURPOSE: pile on octaves at half weight each and the sum converges on its mean
// (it is an average of independent fields), which is exactly the flat grey a fog must not be. Three
// octaves dominated by a 3x3 lattice gives big, separable lobes with room between them.
const NOISE_OCTAVES: Array<{ freq: number; weight: number }> = [
    { freq: 3, weight: 1 },
    { freq: 6, weight: 0.45 },
    { freq: 12, weight: 0.2 },
]
// Three independent fields, baked into R, G and B. Independent is the point: the warp needs a genuine
// 2D vector, and a greyscale texture would only ever displace along the diagonal.

/**
 * Night fog: a soft, drifting, parallaxing haze, drawn by one shader on one quad.
 *
 * WHY A SHADER AND NOT SPRITES. Layered translucent noise sprites tend towards a flat grey wash --
 * alpha compositing three veils is a weighted SUM, and sums of noise converge on their mean. Fog reads
 * as fog because of its holes, so the three fields here are MULTIPLIED, in one pass, which keeps the
 * gaps and turns them into tendrils. A product is not something alpha blending can express, so this is
 * the one thing here that genuinely needs its own fragment shader rather than being an arrangement of
 * sprites.
 *
 * ONE QUAD, NOT A FILTER. A pixi Filter would give the same shader a free fullscreen quad, but filters
 * allocate a render target and copy the framebuffer through it -- on the integrated GPUs this has to
 * run on, that copy costs more than the fog does. A Mesh with a custom Shader draws straight into the
 * scene: one draw call, three texture fetches per pixel, no render target, no readback.
 *
 * PRECISION, WHICH IS WHERE THIS KIND OF SHADER USUALLY GOES WRONG. World coordinates here run into
 * the thousands of pixels, and a mediump float carries about ten bits of mantissa -- pass a world
 * position into a fragment shader and it quantises into visible steps. So no large number ever reaches
 * the shader: the quad's vertices are CAMERA-RELATIVE (a few hundred px, placed by the mesh transform)
 * and each layer's scroll offset is reduced to its fractional part in JS, in double precision, before
 * being handed over. Dropping a whole number of noise tiles is free precisely because the texture
 * wraps. What the shader sees is `vRel * uInv + uOff`, all of it small.
 *
 * WHERE IT DRAWS. Inside the world surface, like the leaves, so it parallaxes with the camera and is
 * clipped by the night visibility mask -- fog is only drawn where the player can see anything at all,
 * which also means the campfire lights the bank around it for free. Its zIndex sits above tiles and
 * entities but below the leaves, so leaves stay crisp against it, and below the sky, so a roof still
 * covers it.
 *
 * ONLY AT NIGHT (config.nightOnly, on by default): the exact complement of the leaves' daylight gate,
 * so the fog rises as the wind drops and there is no hour that has both or neither. See day_gate.ts.
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.nightFog = true | false        force it on in ANY room at any hour, or off in a foggy one
 *   window.nightFogIntensity = 0.35       peak opacity (the "light" in light fog)
 *   window.nightFogSpeed = 14             drift, world px/sec
 *   window.nightFogDirection = 22         drift heading in degrees (0 = east, 90 = south)
 *   window.nightFogLow = 0.5              below this there is no fog at all; raise for sparser wisps
 *   window.nightFogHigh = 0.74            above this the bank is solid; widen the gap for softer edges
 *   window.nightFogWarp = 0.16            how hard the wisps curl; 0 is round blobs
 */
export class NightFog {
    readonly container: Container

    // Above tiles and entities, below the leaves (900000) and the sky surface (1000000).
    static readonly Z_INDEX = 890000

    // Defaults for anything a room leaves out. Deliberately faint: this is "light fog", and at night
    // it is competing with the darkness mask for the same contrast budget.
    private static readonly DEFAULT_INTENSITY = 0.35
    private static readonly DEFAULT_SPEED = 11
    private static readonly DEFAULT_DIRECTION_DEG = 22
    // The silhouette threshold, and the whole difference between wisps and a veil. LOW sits ABOVE the
    // field's mean, so most of the field falls under it and is simply not there; HIGH is close enough
    // above it to keep the edges soft rather than cut out. Drop LOW towards 0 and the fog goes back to
    // being a hazy filter over everything.
    private static readonly DEFAULT_LOW = 0.5
    private static readonly DEFAULT_HIGH = 0.74
    // How far the warp field drags the sample, in noise-tile units. This is what curls a round blob
    // into a wisp; too much and the bank shears into streaks.
    private static readonly DEFAULT_WARP = 0.16
    private static readonly DEFAULT_COLOR = 0xc8d4e0

    // The quad is grown this far past the visible rectangle, so nothing shows at the edge if the
    // camera moves between the corners being read and the frame being drawn.
    private static readonly MARGIN = TileSize

    private mesh: Mesh<Shader> | undefined
    private vertices = new Float32Array(8)
    private vertexBuffer: Buffer | undefined
    private noise: Texture | undefined
    private running = false
    private startMs = 0

    // Reused scratch, so a per-frame corner read allocates nothing.
    private readonly probe = new Point()
    private readonly topLeft = new Point()
    private readonly bottomRight = new Point()
    private readonly color = new Float32Array(3)

    /**
     * @param surface the world surface -- the fog goes inside it, and is also asked (via toLocal) what
     *        world rectangle is currently on screen, which is the one way to get that right through the
     *        camera's edge clamping, the 2x zoom and screen shake.
     * @param screen the renderer's screen rectangle.
     * @param bake builds the tiling noise texture; injected so the simulation can be tested headless.
     */
    constructor(
        private surface: Container,
        private screen: { width: number; height: number },
        private bake: () => Texture,
    ) {
        const container = new Container()
        container.zIndex = NightFog.Z_INDEX
        container.interactiveChildren = false
        container.visible = false
        this.container = container
    }

    /**
     * Draw one frame of fog, or take it down. `brightness` is the client's interpolated daylight, or
     * undefined for a room with no day cycle (which counts as day, so no fog).
     *
     * Cheap to call every frame: a room with no fog, or any hour that is not night, costs one boolean.
     */
    update = (config: NightFogConfig | null, brightness?: number, sceneElevation = 0) => {
        const resolved = this.resolve(config, brightness)
        if (!resolved) {
            this.stop()
            return
        }

        const mesh = this.ensureMesh()
        if (!mesh) {
            return
        }

        if (!this.running) {
            this.running = true
            this.container.visible = true
            // Time is measured from the moment the bank rolls in, not from page load, so the fog does
            // not arrive already scrolled a long way along.
            this.startMs = performance.now()
        }

        // What is on screen right now, in world coordinates, straight from the live transform. Reading
        // the camera's own modifiers instead would be wrong at the map edges, where the camera stops
        // following the player and the modifier is left undefined.
        this.probe.set(0, 0)
        this.surface.toLocal(this.probe, undefined, this.topLeft)
        this.probe.set(this.screen.width, this.screen.height)
        this.surface.toLocal(this.probe, undefined, this.bottomRight)

        const left = Math.min(this.topLeft.x, this.bottomRight.x) - NightFog.MARGIN
        const right = Math.max(this.topLeft.x, this.bottomRight.x) + NightFog.MARGIN
        const top = Math.min(this.topLeft.y, this.bottomRight.y) - NightFog.MARGIN
        const bottom = Math.max(this.topLeft.y, this.bottomRight.y) + NightFog.MARGIN
        const cameraX = (left + right) / 2
        const cameraY = (top + bottom) / 2

        // Camera-relative corners; the mesh transform puts them back in the world. This is the
        // precision guard -- these are hundreds of pixels, never thousands.
        this.vertices[0] = left - cameraX
        this.vertices[1] = top - cameraY
        this.vertices[2] = right - cameraX
        this.vertices[3] = top - cameraY
        this.vertices[4] = right - cameraX
        this.vertices[5] = bottom - cameraY
        this.vertices[6] = left - cameraX
        this.vertices[7] = bottom - cameraY
        this.vertexBuffer?.update(this.vertices)
        mesh.position.set(cameraX, cameraY)

        const seconds = (performance.now() - this.startMs) / 1000
        const driftX = Math.cos(resolved.directionRad) * resolved.speed * seconds
        const driftY = Math.sin(resolved.directionRad) * resolved.speed * seconds

        const uniforms = mesh.shader.uniforms
        for (let i = 0; i < LAYERS.length; i++) {
            const { size, parallax } = LAYERS[i]
            const inv = 1 / size
            // uv = (world - camera * (1 - parallax) - drift) / size, rearranged around the
            // camera-relative vertex the shader actually has: rel * inv + (camera * parallax - drift) *
            // inv. The constant half is computed HERE, in double precision, and reduced to its
            // fractional part -- dropping whole noise tiles is invisible because the texture wraps, and
            // it is what keeps a big world from quantising the fog into steps.
            const offX = (cameraX * parallax - driftX) * inv
            const offY = ((cameraY - sceneElevation) * parallax - driftY) * inv
            uniforms[`uInv${i}`][0] = inv
            uniforms[`uInv${i}`][1] = inv
            uniforms[`uOff${i}`][0] = offX - Math.floor(offX)
            uniforms[`uOff${i}`][1] = offY - Math.floor(offY)
        }

        uniforms.uIntensity = resolved.intensity
        uniforms.uLow = resolved.low
        uniforms.uHigh = resolved.high
        uniforms.uWarp = resolved.warp
        this.color[0] = ((resolved.color >> 16) & 0xff) / 255
        this.color[1] = ((resolved.color >> 8) & 0xff) / 255
        this.color[2] = (resolved.color & 0xff) / 255
    }

    // Exposed for tests: the opacity the fog is currently drawing at, 0 when it is not drawing.
    getIntensity = (): number => (this.running && this.mesh ? (this.mesh.shader.uniforms.uIntensity as number) : 0)

    private stop = () => {
        if (!this.running) {
            return
        }
        this.running = false
        this.container.visible = false
    }

    /**
     * The room's config folded together with the defaults, the console overrides and the hour -- or
     * null if there is to be no fog, which is the common case and must stay the cheap one.
     */
    private resolve = (config: NightFogConfig | null, brightness?: number) => {
        const w = window as any
        const forced = typeof w.nightFog === "boolean" ? (w.nightFog as boolean) : undefined
        const enabled = forced !== undefined ? forced : !!config?.enabled
        if (!enabled) {
            return null
        }

        const num = (override: any, value: number | undefined, fallback: number) =>
            typeof override === "number" ? override : typeof value === "number" ? value : fallback

        // Forcing the fog on from the console also lifts the night gate; there is no way to look at it
        // at noon otherwise, and waiting for the hour is a four-minute round trip.
        const nightOnly = forced === true ? false : config?.nightOnly !== false
        const night = nightOnly ? nightFraction(brightness) : 1
        if (night <= 0) {
            return null
        }

        return {
            // The hour scales the opacity and nothing else: unlike the leaves there is no count to
            // thin, so the bank simply thickens as the light goes.
            intensity:
                Math.min(1, Math.max(0, num(w.nightFogIntensity, config?.intensity, NightFog.DEFAULT_INTENSITY))) *
                night,
            speed: num(w.nightFogSpeed, config?.speed, NightFog.DEFAULT_SPEED),
            directionRad:
                (num(w.nightFogDirection, config?.directionDeg, NightFog.DEFAULT_DIRECTION_DEG) * Math.PI) / 180,
            low: num(w.nightFogLow, config?.low, NightFog.DEFAULT_LOW),
            high: num(w.nightFogHigh, config?.high, NightFog.DEFAULT_HIGH),
            warp: num(w.nightFogWarp, config?.warp, NightFog.DEFAULT_WARP),
            color: typeof config?.color === "number" ? config.color : NightFog.DEFAULT_COLOR,
        }
    }

    /**
     * Build the mesh on the first foggy frame rather than at construction: a room that never fogs
     * should not pay for the noise bake, the buffer or the shader compile, and doing it here means the
     * cost lands once, on the night it is first needed.
     */
    private ensureMesh = (): Mesh<Shader> | undefined => {
        if (this.mesh) {
            return this.mesh
        }
        let noise: Texture
        try {
            noise = this.bake()
        } catch (e) {
            // Fog is ambience. If the bake fails there simply is no fog.
            return undefined
        }
        this.noise = noise

        this.vertexBuffer = new Buffer(this.vertices, false, false)
        const geometry = new Geometry()
            .addAttribute("aVertexPosition", this.vertexBuffer, 2)
            .addIndex([0, 1, 2, 0, 2, 3])

        const shader = Shader.from(NightFog.VERTEX, NightFog.FRAGMENT, {
            uNoise: noise,
            uInv0: new Float32Array(2),
            uInv1: new Float32Array(2),
            uInv2: new Float32Array(2),
            uOff0: new Float32Array(2),
            uOff1: new Float32Array(2),
            uOff2: new Float32Array(2),
            uIntensity: 0,
            uLow: NightFog.DEFAULT_LOW,
            uHigh: NightFog.DEFAULT_HIGH,
            uWarp: NightFog.DEFAULT_WARP,
            uColor: this.color,
        })

        const mesh = new Mesh(geometry, shader)
        this.mesh = mesh
        this.container.addChild(mesh)
        return mesh
    }

    // Vertex positions arrive camera-relative and go straight out as the varying; a vertex shader is
    // always highp, so the transform itself is safe whatever the world coordinates are.
    private static readonly VERTEX = `
        precision highp float;

        attribute vec2 aVertexPosition;

        uniform mat3 projectionMatrix;
        uniform mat3 translationMatrix;

        varying vec2 vRel;

        void main() {
            vRel = aVertexPosition;
            gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
        }
    `

    private static readonly FRAGMENT = `
        precision mediump float;

        varying vec2 vRel;

        uniform sampler2D uNoise;
        uniform vec2 uInv0;
        uniform vec2 uInv1;
        uniform vec2 uInv2;
        uniform vec2 uOff0;
        uniform vec2 uOff1;
        uniform vec2 uOff2;
        uniform float uIntensity;
        uniform float uLow;
        uniform float uHigh;
        uniform float uWarp;
        uniform vec3 uColor;

        void main() {
            // The coarse layer does double duty: it is both the slowest-parallaxing body of the bank
            // and the source of the warp. Its green and blue channels are two INDEPENDENT fields, so
            // the displacement is a real 2D vector -- a greyscale texture would give g == b and shear
            // everything along one diagonal.
            vec3 coarse = texture2D(uNoise, vRel * uInv0 + uOff0).rgb;
            vec2 warp = (coarse.gb - 0.5) * uWarp;

            // DOMAIN WARPING is what turns blobs into wisps: the mass is sampled at a position that has
            // itself been dragged around by a slower field, so its lobes stretch and curl the way
            // something carried on moving air does, instead of sitting there as soft circles.
            float body = texture2D(uNoise, vRel * uInv1 + uOff1 + warp).r;
            float detail = texture2D(uNoise, vRel * uInv2 + uOff2 + warp * 0.5).g;

            // Build ONE field out of the three, then cut it. Mixing the fine layer in BEFORE the
            // threshold is what makes an edge billow: the silhouette wanders at the fine layer's
            // frequency and breaks into lobes. Multiplying it in afterwards -- the obvious thing, and
            // what this did first -- only shades the inside and leaves a smooth, soapy outline.
            float field = body * 0.55 + coarse.r * 0.25 + detail * 0.2;

            // THE SILHOUETTE, and the whole difference between fog and a hazy filter. uLow sits at the
            // field's median, so half the screen falls below it and has no fog on it AT ALL -- the
            // empty gaps are what the eye reads as separate wisps. A threshold low enough to touch
            // every pixel is a grey filter, however faint it is made.
            float mass = smoothstep(uLow, uHigh, field);

            // Structure inside a wisp, so it is not a flat cut-out: lighter cores, thinner fringes.
            float density = mass * (0.6 + 0.4 * detail);

            float a = density * uIntensity;
            // Premultiplied: pixi's NORMAL blend expects the colour already multiplied by the alpha.
            gl_FragColor = vec4(uColor * a, a);
        }
    `

    /**
     * Three independent tileable value-noise fields, each in 0..1, row-major.
     *
     * Baked rather than computed per pixel in the shader: procedural fbm would cost forty-odd ALU ops a
     * pixel every frame forever, where this is a few thousand CPU operations once. It has to TILE,
     * because the whole scroll scheme rests on GL_REPEAT, so every lattice lookup wraps -- get that
     * wrong and there is a hard seam across the world every few tiles, and it only shows up there.
     *
     * Separate from bakeNoise so the fields can be checked without a GPU.
     */
    static buildNoiseField = (): { size: number; channels: Float32Array[] } => {
        const channels: Float32Array[] = []

        for (let c = 0; c < 3; c++) {
            const field = new Float32Array(NOISE_SIZE * NOISE_SIZE)

            for (const { freq, weight } of NOISE_OCTAVES) {
                // The lattice this octave interpolates. Indices wrap, which is what makes it tile.
                const lattice = new Float32Array(freq * freq)
                for (let i = 0; i < lattice.length; i++) {
                    lattice[i] = Math.random()
                }

                const cell = NOISE_SIZE / freq
                for (let y = 0; y < NOISE_SIZE; y++) {
                    for (let x = 0; x < NOISE_SIZE; x++) {
                        const fx = x / cell
                        const fy = y / cell
                        const x0 = Math.floor(fx)
                        const y0 = Math.floor(fy)
                        const x1 = (x0 + 1) % freq
                        const y1 = (y0 + 1) % freq
                        // Smoothstep rather than linear interpolation: linear leaves the lattice
                        // visible as a grid of creases, which on a stretched fog layer is unmissable.
                        const tx = fx - x0
                        const ty = fy - y0
                        const sx = tx * tx * (3 - 2 * tx)
                        const sy = ty * ty * (3 - 2 * ty)
                        const wx0 = x0 % freq
                        const wy0 = y0 % freq
                        const a = lattice[wy0 * freq + wx0]
                        const b = lattice[wy0 * freq + x1]
                        const d = lattice[y1 * freq + wx0]
                        const e = lattice[y1 * freq + x1]
                        const top = a + (b - a) * sx
                        const bot = d + (e - d) * sx
                        field[y * NOISE_SIZE + x] += (top + (bot - top) * sy) * weight
                    }
                }
            }

            // Normalise on the field's ACTUAL range rather than the weight sum. A sum of octaves never
            // reaches its theoretical extremes, so dividing by the weights leaves everything bunched in
            // the middle -- which is the flat grey this effect exists to avoid. Stretching to the real
            // min/max is free contrast, and the silhouette threshold depends on having it.
            let min = Infinity
            let max = -Infinity
            for (let i = 0; i < field.length; i++) {
                min = Math.min(min, field[i])
                max = Math.max(max, field[i])
            }
            const span = max - min || 1
            for (let i = 0; i < field.length; i++) {
                field[i] = (field[i] - min) / span
            }
            channels.push(field)
        }

        return { size: NOISE_SIZE, channels }
    }

    // Upload the three fields as the R, G and B channels of one wrapping texture -- one fetch hands the
    // shader three independent values, which is what keeps this to three texture reads a pixel.
    static bakeNoise = (): Texture => {
        const { size, channels } = NightFog.buildNoiseField()

        const canvas = document.createElement("canvas")
        canvas.width = size
        canvas.height = size
        const context = canvas.getContext("2d")
        if (!context) {
            throw new Error("no 2d context for the fog noise bake")
        }
        const image = context.createImageData(size, size)
        const byte = (value: number) => Math.max(0, Math.min(255, Math.round(value * 255)))
        for (let i = 0; i < channels[0].length; i++) {
            image.data[i * 4] = byte(channels[0][i])
            image.data[i * 4 + 1] = byte(channels[1][i])
            image.data[i * 4 + 2] = byte(channels[2][i])
            image.data[i * 4 + 3] = 255
        }
        context.putImageData(image, 0, 0)

        const texture = Texture.from(canvas)
        // The one non-negotiable: every layer scrolls indefinitely, so the field has to wrap. This is
        // also why NOISE_SIZE is a power of two -- WebGL 1 refuses to repeat anything else.
        texture.baseTexture.wrapMode = WRAP_MODES.REPEAT
        return texture
    }
}
